import React, { useMemo, useState } from "react";
import { Table, Modal, Button } from "antd";
import { ExportToExcel } from "../../../../utils";

export const TopClientsTable = ({ data }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsModalVisible, setItemsModalVisible] = useState(false);

  const filteredData = data?.filter(
    (order) =>
      order.phoneNumber !== "111111111" && order.phoneNumber !== "123456789"
  );

  const customers = {};

  filteredData?.forEach((order) => {
    
    const { phoneNumber, customerName, products, saleAmount, dateStart } = order;

    if (!customers[phoneNumber]) {
      customers[phoneNumber] = {
        customerName,
        totalSpent: 0,
        totalItems: 0,
        totalOrders: 0,
        lastOrderDate: dateStart.toDate(),
        itemsCount: {},
      };
    }

    const customer = customers[phoneNumber];
    customer.totalSpent += saleAmount;
    customer.totalItems += products?.reduce(
      (total, item) => total + item.quantity,
      0
    );
    customer.totalOrders += 1;
    if (dateStart.toDate() > customer.lastOrderDate) {
      customer.lastOrderDate = dateStart.toDate();
    }

    products?.forEach((item) => {
      const key = `${item.category}-${item.brand}`;
      if (!customer.itemsCount[key]) {
        customer.itemsCount[key] = { ...item, quantity: 0 };
      }
      customer.itemsCount[key].quantity += item.quantity;
    });
  });

  const dataSource = Object.keys(customers)?.map((phoneNumber) => ({
    key: phoneNumber,
    phoneNumber,
    customerName: customers[phoneNumber].customerName,
    totalSpent: customers[phoneNumber].totalSpent.toFixed(2),
    totalItems: customers[phoneNumber].totalItems,
    totalOrders: customers[phoneNumber].totalOrders,
    lastOrderDate: customers[phoneNumber].lastOrderDate,
  }));

  dataSource.sort(
    (a, b) => parseFloat(b.totalSpent) - parseFloat(a.totalSpent)
  );

  const handleShowItems = (customerName) => {
    let phoneNumber = "";
    for (let i = 0; i < filteredData.length; i++) {
      if (filteredData[i].customerName === customerName) {
        phoneNumber = filteredData[i].phoneNumber;
        break;
      }
    }

    if (phoneNumber === "111111111" || phoneNumber === "123456789") {
      console.error(`Invalid phone number for customer: ${customerName}`);
      return;
    }

    const ordersFromCustomer = filteredData?.filter(
      (order) => order.phoneNumber === phoneNumber
    );

    if (ordersFromCustomer.length > 0) {
      const itemsCount = {};

      ordersFromCustomer?.forEach((order) => {
        order.products?.forEach((item) => {
          const key = `${item.category}-${item.brand}`;

          if (!itemsCount[key]) {
            itemsCount[key] = { ...item, quantity: 0 };
          }

          itemsCount[key].quantity += item.quantity;
        });
      });

      setSelectedItems([]);
      setSelectedItems(Object.values(itemsCount));
      setItemsModalVisible(true);
    } else {
      console.error(`No customer found with name: ${customerName}`);
    }
  };

  const columns = [
    {
      title: "Nombre del Cliente",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Total Gastado",
      dataIndex: "totalSpent",
      key: "totalSpent",
      render: (text) => {
        const total = parseFloat(text);
        if (!isNaN(total)) {
          return `S/ ${total}`;
        }
        return "";
      },
      sorter: (a, b) => a.totalSpent - b.totalSpent,
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Total de Órdenes",
      dataIndex: "totalOrders",
      key: "totalOrders",
      sorter: (a, b) => a.totalOrders - b.totalOrders,
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },

    {
      title: "Total de Productos",
      dataIndex: "totalItems",
      key: "totalItems",
      sorter: (a, b) => a.totalItems - b.totalItems,
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Última Orden",
      dataIndex: "lastOrderDate",
      key: "lastOrderDate",
      render: (text) => {
        const date = new Date(text);
        return date.toLocaleDateString();
      },
    },
    {
      title: "Productos",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => (
        <Button
          onClick={() => handleShowItems(record.customerName)}
          className="btnCancel"
        >
          Detalles
        </Button>
      ),
    },
  ];

  const excelColumns = [
    {
      title: "Nombre",
      dataIndex: "customerName",
      key: "customerName",
      width: 200,
    },
    {
      title: "Monto de compra",
      dataIndex: "saleAmount",
      key: "saleAmount",
      width: 200,
    },
    {
      title: "Número de órdenes",
      dataIndex: "totalOrders",
      key: "totalOrders",
      width: 200,
    },
    {
      title: "Número de productos",
      dataIndex: "totalItems",
      key: "totalItems",
      width: 200,
    },
    {
      title: "Fechas de compras",
      dataIndex: "dates",
      key: "dates",
      width: 200,
    },
    { title: "Productos", dataIndex: "products", key: "products", width: 500 },
  ];

  const excelData = dataSource.map((row) => {
    const ordersFromCustomer = data.filter(
      (order) => order.phoneNumber === row.phoneNumber
    );

    const dates = ordersFromCustomer
      .map((order) =>
        new Date(order.dateStart.seconds * 1000).toLocaleDateString()
      )
      .join(", ");

    const productCounts = {};
    ordersFromCustomer.forEach((order) => {
      order.products.forEach((item) => {
        const key = `${item.category}-${item.brand}-${item.model}`;
        if (!productCounts[key]) {
          productCounts[key] = 0;
        }
        productCounts[key] += 1;
      });
    });

    const products = Object.entries(productCounts)
      .map(([key, count]) => `${count} x ${key}`)
      .join(", ");

    let totalPurchaseAmount = 0;
    ordersFromCustomer.forEach((order) => {
      if (typeof order.saleAmount === "number") {
        totalPurchaseAmount += order.saleAmount;
      }
    });

    return {
      ...row,
      dates,
      products,
      saleAmount: `S/ ${totalPurchaseAmount.toFixed(2)}`,
    };
  });

  const customerSizeData = useMemo(()=>{
    let rowData = {};
    const exportDataArray = [];
    data.forEach((order)=>{
      if(!rowData[order.customerName]){
        rowData[order.customerName] = {};
      }
      if(!rowData[order.customerName][order.phoneNumber]){
        rowData[order.customerName][order.phoneNumber] = {};
      }

      order.products.forEach((item)=>{
        if(!rowData[order.customerName][order.phoneNumber][item.category]){
          rowData[order.customerName][order.phoneNumber][item.category] = {};
        }
        if(!rowData[order.customerName][order.phoneNumber][item.category][item.brand]){
          rowData[order.customerName][order.phoneNumber][item.category][item.brand] = {};
        }
        if(!rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model]){
          rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model] = {};
        }
        if(!rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model][item.size]){
          rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model][item.size] = {
            totalSales: 0,
            totalItems: 0,
            totalOrders: 0,
          };
        }

        rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model][item.size].totalSales +=
        item.total * item.quantity;
        rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model][item.size].totalItems += 
        item.quantity;
        rowData[order.customerName][order.phoneNumber][item.category][item.brand][item.model][item.size].totalOrders++;
      });
    });
    // eslint-disable-next-line
    Object.keys(rowData)?.map((customerName)=>{
      // eslint-disable-next-line
      Object.keys(rowData[customerName])?.map((phoneNumber)=>{
        // eslint-disable-next-line
        Object.keys(rowData[customerName][phoneNumber])?.map((category)=>{
          // eslint-disable-next-line
          Object.keys(rowData[customerName][phoneNumber][category])?.map((brand)=>{
            // eslint-disable-next-line
            Object.keys(rowData[customerName][phoneNumber][category][brand])?.map((model)=>{
              // eslint-disable-next-line
              Object.keys(rowData[customerName][phoneNumber][category][brand][model])?.map((size)=>{
                const record = {
                  customerName,
                  phoneNumber,
                  category,
                  brand,
                  model,
                  size,
                  ...rowData[customerName][phoneNumber][category][brand][model][size],
                };
                exportDataArray.push(record);
              });
            });
          });
        });
      });
    });
    return exportDataArray;
  },[data]);

  const exportData = customerSizeData?.map((item) => {
    console.log(item);
    return ({
      Cliente:item.customerName,
      Telefono:item.phoneNumber,
      Categoria:item.category,
      Marca:item.brand,
      Modelo: item.model,
      Talla: item.size,
      "Ventas totales": `$${item.totalSales.toFixed(2)}`,
      "Cantidad de productos": item.totalItems,
      "Cantidad de ordenes": item.totalOrders,
    });
  });

  const exportColumns = [
    { title: 'Cliente', dataIndex: 'Cliente', key: 'Cliente' },
    { title: 'Telefono', dataIndex: 'Telefono', key: 'Telefono' },
    { title: 'Categoria', dataIndex: 'Categoria', key: 'Categoria' },
    { title: 'Marca', dataIndex: 'Marca', key: 'Marca' },
    { title: 'Modelo', dataIndex: 'Modelo', key: 'Modelo' },
    { title: 'Talla', dataIndex: 'Talla', key: 'Talla' },
    { title: 'Ventas totales', dataIndex: 'Ventas totales', key: 'Ventas totales' },
    { title: 'Cantidad de productos', dataIndex: 'Cantidad de productos', key: 'Cantidad de productos' },
    { title: 'Cantidad de ordenes', dataIndex: 'Cantidad de ordenes', key: 'Cantidad de ordenes' },
  ];

  return (
    <div className="w-full pt-4 pr-4 pl-4">
      <div className="overflow-x-auto h-96">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
        />
      </div>
      <Modal
        title="Detalle"
        open={itemsModalVisible}
        onCancel={() => setItemsModalVisible(false)}
        footer={null}
      >
        {selectedItems && (
          <div className="overflow-x-auto h-96">
            <Table
              dataSource={selectedItems}
              columns={[
                {
                  title: "Categoría",
                  dataIndex: "category",
                  key: "category",
                },
                {
                  title: "Marca",
                  dataIndex: "brand",
                  key: "brand",
                },
                {
                  title: "Cantidad",
                  dataIndex: "quantity",
                  key: "quantity",
                },
              ]}
              pagination={false}
            />
          </div>
        )}
      </Modal>
      <div
        className="flex justify-end my-2 mr-5"
      >
        <ExportToExcel
          data={excelData}
          columns={excelColumns}
          fileName="topClientes"
        />
      </div>
      <div
        className="flex justify-end my-2 mr-5"
      >
        <ExportToExcel
          data={exportData}
          columns={exportColumns}
          fileName="clientesTalla"
          name="Clientes x tallas"
        />
      </div>
    </div>
  );
};
